import { cn } from '@sohanemon/utils';
import { Iconify } from '@sohanemon/utils/components';
import { m } from '@sohanscript/i18n';
import { isRtl } from '@sohanscript/i18n/locale-meta';
import { Motion } from '@sohanscript/ui/motion';
import { AnimatePresence } from 'motion/react';
import type * as React from 'react';
import { useCallback, useState } from 'react';
import { Button } from '#/components/ui/button.tsx';
import { useWindowStore } from '#/lib/store/window-manager.ts';

interface DockApp {
	id: string;
	name: string;
	icon: string;
}

type UbuntuDashProps = React.ComponentProps<'div'> & {
	apps: DockApp[];
	onAppClick: (appId: string) => void;
	showAppsButton?: boolean;
};

type DashItemProps = React.ComponentProps<'div'> & {
	app: DockApp;
	isClicked: boolean;
	onClick: () => void;
};

function DashItem({
	app,
	isClicked,
	onClick,
	className,
	...props
}: DashItemProps) {
	const { windows } = useWindowStore();
	const win = windows.find((w) => w.id === app.id);
	const isMinimized = win?.status === 'minimized';
	const isActive = win?.status === 'open' || win?.status === 'maximized';
	const showIndicator = isActive || isMinimized;

	return (
		<div
			className={cn('relative flex shrink-0 items-center', className)}
			{...props}
		>
			{showIndicator && (
				<div
					className={cn(
						'absolute inset-x-0 bottom-0 mx-auto h-px rounded bg-primary',
						isMinimized && 'bg-primary/50',
					)}
				/>
			)}

			<Button
				size="icon"
				variant="opacity"
				onClick={onClick}
				tabIndex={0}
				tooltip={app.name}
				tooltipContentProps={{ side: 'right' }}
				style={{
					transform: isClicked ? 'scale(0.9)' : 'scale(1)',
				}}
			>
				<Iconify icon={app.icon} className="size-7" />
			</Button>
		</div>
	);
}

export function UbuntuDash({
	apps,
	onAppClick,
	showAppsButton = true,
	className,
	...props
}: UbuntuDashProps) {
	const [clickedId, setClickedId] = useState<string | null>(null);
	const [showAppGrid, setShowAppGrid] = useState(false);

	const handleClick = useCallback(
		(id: string) => {
			setClickedId(id);
			onAppClick(id);
			setTimeout(() => setClickedId(null), 150);
		},
		[onAppClick],
	);

	const toggleAppGrid = useCallback(() => {
		setShowAppGrid((prev) => !prev);
	}, []);

	const handleAppGridClick = useCallback(
		(id: string) => {
			handleClick(id);
			setShowAppGrid(false);
		},
		[handleClick],
	);

	return (
		<>
			<div
				className={cn(
					'relative z-50 flex flex-col items-center gap-3 border-border/50 border-r bg-card/15 p-3 shadow-xl backdrop-blur-sm',
					className,
				)}
				{...props}
			>
				{apps.map((app) => (
					<DashItem
						key={app.id}
						app={app}
						isClicked={clickedId === app.id}
						onClick={() => handleClick(app.id)}
					/>
				))}

				{showAppsButton && (
					<>
						<div className="mt-auto h-px w-1/2 bg-border" />
						<Button
							size="icon"
							variant="opacity"
							onClick={toggleAppGrid}
							tooltipContentProps={{
								side: isRtl() ? 'left' : 'right',
							}}
							tabIndex={0}
							tooltip={m.ubuntu_show_apps()}
							className={cn(showAppGrid && 'bg-primary/20')}
							icon="fontisto:nav-icon-grid-a"
						/>
					</>
				)}
			</div>

			{/* App Grid Overlay */}
			<AnimatePresence>
				{showAppGrid && (
					<Motion
						initial={{ opacity: 0, scale: 0.95 }}
						animate={{ opacity: 1, scale: 1 }}
						exit={{ opacity: 0, scale: 0.95 }}
						transition={{ duration: 0.2 }}
						className="fixed inset-0 left-16 z-40 flex items-center justify-center bg-background/80 p-8 backdrop-blur-md"
						onClick={() => setShowAppGrid(false)}
					>
						<Motion
							className="grid-auto-fit-32 grid max-h-[80vh] max-w-4xl place-content-center gap-4 p-4"
							onClick={(e) => e.stopPropagation()}
						>
							{apps.map((app, index) => (
								<Motion
									key={app.id}
									initial={{ opacity: 0, y: 20 }}
									animate={{ opacity: 1, y: 0 }}
									transition={{ delay: index * 0.02 }}
									className="flex cursor-pointer flex-col items-center gap-2 rounded-lg p-4 hover:bg-primary/10"
									onClick={() => handleAppGridClick(app.id)}
								>
									<div className="corner-bevel flex size-16 items-center justify-center rounded-xl bg-card/50 shadow-lg">
										<Iconify icon={app.icon} className="size-10" />
									</div>
									<span className="max-w-full truncate text-center font-medium text-sm">
										{app.name}
									</span>
								</Motion>
							))}
						</Motion>
					</Motion>
				)}
			</AnimatePresence>
		</>
	);
}

export type { DockApp, UbuntuDashProps };
